import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Menu,
  Badge,
  Divider,
  Button,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { LoginType, NotificationPriorityType, StudentGrade, UserTypeId } from '../const';
import CommonCard from './CommonCard';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setLoading } from '../../features/authSlice';
import { ArrowDropDown, Person, Search } from '@mui/icons-material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CommonFeatureIcon from './CommonFeatureIcon';
import { getNotification, getNotificationCount } from '../../api/notificationApi';
import { showNotification } from './Notification/ToastifyNotification';
import moment from 'moment';
import { getParents } from '../../api/parentApi';
import { setParentList } from '../../features/parentSlice';
import CommonButton from './CommonButton';
import CommonDialog from './CommonDialog';

const Header = () => {
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const { parentList } = useSelector((state) => state.parent);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorE2, setAnchorE2] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationData, setNotificationData] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [totals, setTotals] = useState(0);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [search, setSearch] = useState(
    localStorage.getItem('college_search') || ''
  );

  const getCount = async () => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const response = await getNotificationCount(user?.user_id);
      setUnreadCount(response.data.unread_count);
      dispatch(setLoading({
        loading: false
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      showNotification(errorMessage, 'error');
      dispatch(setLoading({
        loading: false
      }));
    }
  }

  const getNotificationData = async () => {
    try {
      if (pageNo > 1) {
        setIsFetchingMore(true);
      } else {
        dispatch(setLoading({ loading: true }));
      }
      const response = await getNotification(user?.user_id, pageNo);
      const newData = response.data.data || [];

      if (newData.length === 0) {
        setHasMore(false);
      } else {
        setNotificationData(prev =>
          pageNo === 1 ? newData : [...prev, ...newData]
        );
      }
      setTotals(response?.data?.pagination.total);
      dispatch(setLoading({ loading: false }));
      setIsFetchingMore(false);
      dispatch(setLoading({
        loading: false
      }));
    } catch (err) {
      setIsFetchingMore(false);
      setNotificationData([]);
      setTotals(0);
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      showNotification(errorMessage, 'error');
      dispatch(setLoading({
        loading: false
      }));
    }
  }

  useEffect(() => {
    if (user?.avatar) {
      const getImage = async () => {
        if (user.login_type === LoginType.EMAIL) {
          const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${user.avatar}&download=false`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const blob = await responseData.blob();
          const imageUrl = URL.createObjectURL(blob);
          setPreviewUrl(imageUrl);
        } else {
          setPreviewUrl(user?.avatar);
        }
      }
      getImage();
    }
    if (user?.user_id) {
      getCount();
      getNotificationData();
    }
  }, [user]);

  const getParentList = async () => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const response = await getParents(user?.student_id);
      dispatch(setParentList({
        list: response.data
      }));
      dispatch(setLoading({
        loading: false
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      showNotification(errorMessage, 'error');
      dispatch(setLoading({
        loading: false
      }));
    }
  }

  useEffect(() => {
    if (user?.student_id) {
      getParentList();
    }
  }, [user?.student_id]);


  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (event) => {
    setAnchorE2(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorE2(null);
  };
  
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        py: 0.5
      }}
    >
      <CommonDialog
        open={openLogoutDialog}
        size="xs"
        content={(
          <center>
            <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
              Are you sure you want to logout? You will need to login again to access your account.
            </Typography>
          </center>
        )}
        actionButtons={(
          <Fragment>
            <CommonButton
              label="Logout"
              size="small"
              background={theme.palette.secondary.main}
              color={theme.palette.primary.main}
              handleClick={async () => {
                setOpenLogoutDialog(false);
                handleProfileMenuClose();
                dispatch(logout());
                navigate('/');
              }}
            />
            <CommonButton
              label="Cancel"
              size="small"
              background={theme.palette.background.main}
              border={`1px solid ${theme.palette.background.light}`}
              handleClick={() => {
                handleProfileMenuClose();
                setOpenLogoutDialog(false);
              }}
            />
          </Fragment>
        )}
      />
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between'
        }}>
        {/* Left side - Logo and Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '60%', gap: 4 }}>
          <Box
            component="img"
            src="/Images/headerlogo.png" // Replace with your logo icon path
            alt="Logo"
            sx={{
              width: 'auto',
              height: '50px',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/dashboard')}
          />
          {/* Navigation Items */}
          <Box sx={{ display: 'flex', width: '100%' }}>
            <Box
              component="img"
              src={location.pathname.includes('dashboard') ? "/Images/activedashboard.png" : "/Images/dashboard.png"} // Replace with your logo icon path
              alt="Logo"
              sx={{
                width: '25%',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/dashboard')}
            />
            <Box
              component="img"
              src={location.pathname.includes('colleges') ? "/Images/activecollege.png" : "/Images/college.png"}
              alt="Logo"
              sx={{
                width: '25%',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/colleges')}
            />
            <Box
              component="img"
              src={location.pathname.includes('course-exams') ? "/Images/activeexams.png" : "/Images/exams.png"}
              alt="Logo"
              sx={{
                width: '25%',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/course-exams')}
            />
            <Box
              component="img"
              src={location.pathname.includes('services') ? "/Images/activeservices.png" : "/Images/services.png"}
              alt="Logo"
              sx={{
                width: '25%',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/services')}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6, width: '40%', justifyContent: 'flex-end' }}>
          <TextField
            placeholder="Search here"
            variant="outlined"
            size="small"
            value={search}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: theme.palette.background.main,
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                },
              },
              '& input': {
                height: '25px',
                fontSize: '14px',
                fontWeight: 500,
                width: '130px'
              }
            }}
            onChange={(e) => {
              const value = e.target.value;

              setSearch(value);
              localStorage.setItem('college_search', value);

              // optional clear
              if (value.length === 0) {
                navigate('/colleges');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = search.trim();

                if (value.length > 0) {
                  navigate(`/colleges?search=${value}`);

                  // call api here if needed
                  // getCollegeList(value)
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      fontSize: 30,
                      color: theme.palette.background.dark,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const value = search.trim();

                      if (value.length > 0) {
                        navigate(`/colleges?search=${value}`);
                      }
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <Box
            component="img"
            src="/Images/trypremium.png" // Replace with your logo icon path
            alt="Logo"
            sx={{
              width: '130px',
              height: '40px',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/subscriptions')}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              size="small"
              sx={{
                color: theme.palette.secondary.main,
                width: '40px',
                height: '40px',
                padding: '6px', // Adjust padding for better centering
              }}
              onClick={handleMenuOpen}
            >
              <Badge
                badgeContent={unreadCount}
                overlap="circular" // Better overlap handling
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    height: '18px',
                    width: '18px',
                    borderRadius: '50%',
                    top: '4px',
                    right: '4px',
                    background: theme.palette.primary.main,
                    color: 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }
                }}
              >
                <NotificationsIcon sx={{ color: theme.palette.background.dark }} />
              </Badge>
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                pl: 1,
                py: '4px',
                pr: 1.5,
                borderRadius: '20px',
                backgroundColor: theme.palette.background.main,
                '&:hover': {
                  backgroundColor: theme.palette.background.main,
                }
              }}
              onClick={handleProfileMenuOpen}
            >
              {previewUrl ? (
                <Avatar
                  src={previewUrl}
                  sx={{
                    width: '33px',
                    height: '33px',
                    backgroundColor: theme.palette.grey[200],
                    cursor: 'pointer',
                  }}
                >
                </Avatar>
              ) : (
                <Avatar
                  sx={{
                    width: '33px',
                    height: '33px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    background: theme.palette.secondary.main,
                    color: theme.palette.primary.main
                  }}
                >
                  {`${user?.first_name?.charAt(0)?.toUpperCase()} ${user?.last_name?.charAt(0)?.toUpperCase()}`}
                </Avatar>
              )}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'secondary.main',
                    ml: 1,
                    mr: 0.5
                  }}
                >
                  Account
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'background.dark',
                    ml: 1,
                    mr: 0.5
                  }}
                >
                  {user?.user_type_id === UserTypeId.Parent ? 'Parent' : 'Student'}
                </Typography>
              </Box>
              <ArrowDropDown sx={{ color: theme.palette.background.dark }} />
            </Box>
          </Box>
        </Box>
      </Toolbar>

      {/* Account Menu Dropdown with Icons */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 2.3,
            minWidth: '300px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #E5E7EB'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 2.1, pt: 1, pb: 2.5 }}>
          {previewUrl ? (
            <Avatar
              src={previewUrl}
              sx={{
                width: '40px',
                height: '40px',
                backgroundColor: theme.palette.grey[200],
                cursor: 'pointer',
              }}
            >
            </Avatar>
          ) : (
            <Avatar
              sx={{
                width: '40px',
                height: '40px',
                fontSize: '0.7rem',
                fontWeight: 600,
                background: theme.palette.secondary.main,
                color: theme.palette.primary.main
              }}
            >
              {`${user?.first_name?.charAt(0)?.toUpperCase()} ${user?.last_name?.charAt(0)?.toUpperCase()}`}
            </Avatar>
          )}
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }} variant='h4'>{`${user?.first_name} ${user?.last_name !== null ? user?.last_name : ''}`}</Typography>
            {user?.user_type_id === UserTypeId.Student && (
              <Typography sx={{ fontSize: 12 }} color="secondary.main">{user?.student_current_grade ? `${StudentGrade.find((a) => a.value === user?.student_current_grade).label}` : ''}</Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <Box
          onClick={() => {
            handleProfileMenuClose();
            navigate('/my-profile');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, pt: 2, pb: 1, cursor: 'pointer' }}>
          <CommonFeatureIcon
            icon={<Person sx={{ fontSize: '20px' }} />}
            height="30px"
            width="30px"
          />
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800 }} variant='h4'>My Profile Manager</Typography>
            <Typography sx={{ fontSize: 10 }} color={theme.palette.background.dark}>Manage your Personal Profile</Typography>
          </Box>
        </Box>
        <Box
          onClick={() => {
            handleProfileMenuClose();
            navigate('/my-parent-account');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, pt: 1, pb: 1, cursor: 'pointer', pr: 1, justifyContent: 'space-between' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <img
              src='/Images/parentaccount.png'
              alt=''
              width="30px"
            />
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 800 }} variant='h4'>My Parent Account</Typography>
              <Typography sx={{ fontSize: 10 }} color={theme.palette.background.dark}>Your parents can view your progress</Typography>
            </Box>
          </Box>
          <Box sx={{ background: theme.palette.success.main, pt: 0.2, pb: 0.2, pl: 1, pr: 1, borderRadius: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: 'white' }}>{parentList.length} Parents</Typography>
          </Box>
        </Box>
        <Box
          onClick={() => {
            handleProfileMenuClose();
            navigate('/my-contact-support');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, pt: 1, pb: 2, cursor: 'pointer' }}>
          <img
            src='/Images/contactsupport.png'
            alt=''
            width="30px"
          />
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800 }} variant='h4'>My Contact Support</Typography>
            <Typography sx={{ fontSize: 10 }} color={theme.palette.background.dark}>Have questions about using the website</Typography>
          </Box>
        </Box>
        <Button
          sx={{
            mt: 1,
            ml: 1.5,
            border: '1px solid lightgray',
            color: theme.palette.text.main,
            width: '92%',
            '&:hover': {
              border: '1px solid lightgray',
              color: theme.palette.text.main,
              background: 'white'
            }
          }}
          variant='outlined'
          onClick={() => {
            setOpenLogoutDialog(true);
          }}
        >
          Logout
        </Button>
      </Menu>
      <Menu
        anchorEl={anchorE2}
        open={Boolean(anchorE2)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 2.3,
            width: '280px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #E5E7EB',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            ml: 2,
            mb: 2,
            mt: 1
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 800 }} variant='h4'>Notifications ({totals})</Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            height: notificationData?.length > 0 ? '300px' : '30px',
            overflow: 'auto'
          }}
          ref={scrollRef}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            const isNearBottom =
              scrollHeight - scrollTop <= clientHeight + 50;

            if (isNearBottom && !isFetchingMore && hasMore) {
              setIsFetchingMore(true);   // 🔒 lock
              setPageNo(prev => prev + 1);
            }
          }}
        >
          {notificationData?.length > 0 ? notificationData?.map((a, index) => (
            <Fragment>
              <CommonCard
                key={index}
                content={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{a.title}</Typography>
                      <Typography sx={{ fontSize: 11 }} color={theme.palette.background.dark}>{a.published_at ? moment(a.published_at).format('YYYY-MM-DD') : ''}</Typography>
                    </Box>
                    {a.priority && (
                      <Box
                        sx={{
                          background: NotificationPriorityType[a.priority] === 'High Priority' || NotificationPriorityType[a.priority] === 'Urgent' ? theme.palette.primary.main : theme.palette.secondary.default,
                          textAlign: 'center',
                          borderRadius: 0.8,
                          width: 120,
                          height: 21,
                          padding: 0.4
                        }}
                      >
                        <Typography sx={{ color: theme.palette.secondary.main, fontSize: 10 }}>{NotificationPriorityType[a.priority]}</Typography>
                      </Box>
                    )}
                  </Box>
                }
                marginTop={0.1}
                paddingBottom="1px"
                padding="12px"
              />
              <Divider />
            </Fragment>
          )) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 0.8 }}>
              <Typography>No Data Found</Typography>
            </Box>
          )}
        </Box>
        {notificationData?.length > 0 && (
          <Button
            sx={{
              mt: 1,
              ml: 1.5,
              border: '1px solid lightgray',
              color: theme.palette.text.main,
              width: '92%',
              '&:hover': {
                border: '1px solid lightgray',
                color: theme.palette.text.main,
                background: 'white'
              }
            }}
            variant='outlined'
            onClick={() => {
              handleMenuClose();
              navigate('/notifications');
            }}
          >
            View More
          </Button>
        )}
      </Menu>
    </AppBar >
  );
};

export default Header;